import type { MatchInput, MatchLabel, MatchResult, ProgramRecord, ProjectBrief } from "./types";

const PROJECT_SYNONYMS: Record<string, string[]> = {
  rag: ["rag", "wissensassistent", "wissen", "daten", "software", "digital", "forschung", "innovation"],
  chatbot: ["chatbot", "kundenservice", "digital", "software", "ki", "künstliche intelligenz"],
  automation: ["automatisierung", "prozess", "digital", "software", "industrie", "innovation"],
  vision: ["bildverarbeitung", "computer vision", "sensorik", "produktion", "industrie", "forschung"],
  forecast: ["daten", "analyse", "prognose", "software", "digital", "innovation"],
  ecommerce: ["digital", "software", "vertrieb", "innovation", "plattform"]
};

const REGION_ALIASES: Record<string, string[]> = {
  "Baden-Württemberg": ["baden-wuerttemberg", "baden-württemberg", "bund"],
  Bayern: ["bayern", "bund"],
  Berlin: ["berlin", "bund"],
  Brandenburg: ["brandenburg", "bund"],
  Bremen: ["bremen", "bund"],
  Hamburg: ["hamburg", "bund"],
  Hessen: ["hessen", "bund"],
  "Mecklenburg-Vorpommern": ["mecklenburg-vorpommern", "bund"],
  Niedersachsen: ["niedersachsen", "bund"],
  "Nordrhein-Westfalen": ["nordrhein-westfalen", "nrw", "bund"],
  "Rheinland-Pfalz": ["rheinland-pfalz", "bund"],
  Saarland: ["saarland", "bund"],
  Sachsen: ["sachsen", "bund"],
  "Sachsen-Anhalt": ["sachsen-anhalt", "bund"],
  "Schleswig-Holstein": ["schleswig-holstein", "bund"],
  "Thüringen": ["thueringen", "thüringen", "bund"]
};

export const DEFAULT_INPUT: MatchInput = {
  projectType: "rag",
  region: "Bayern",
  companySize: "mittel",
  industry: "Maschinenbau",
  stage: "konzept",
  budget: "100k-500k",
  costTypes: ["software", "beratung"],
  innovationLevel: "mittel"
};

export function matchPrograms(programs: ProgramRecord[], input: MatchInput, limit = 8): MatchResult[] {
  return programs
    .map((program) => scoreProgram(program, input))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

export function scoreProgram(program: ProgramRecord, input: MatchInput): MatchResult {
  const reasons: string[] = [];
  const missing: string[] = [];
  let score = 0;

  const haystack = [
    program.title,
    program.summary,
    program.teaser,
    program.keywords.join(" "),
    program.categories.join(" "),
    program.eligibleCosts.join(" ")
  ]
    .join(" ")
    .toLowerCase();

  const regionSignals = REGION_ALIASES[input.region] ?? [input.region.toLowerCase(), "bund"];
  const regionMatch =
    program.level === "Bund" ||
    regionSignals.some((signal) => program.region.toLowerCase().includes(signal) || program.sourcePath.toLowerCase().includes(signal));
  if (regionMatch) {
    score += 22;
    reasons.push(program.level === "Bund" ? "Bundesprogramm, grundsätzlich bundesweit relevant." : `Fördergebiet passt zu ${input.region}.`);
  } else {
    score -= 35;
    reasons.push(`Fördergebiet wirkt nicht passend zu ${input.region}.`);
  }

  const projectSignals = PROJECT_SYNONYMS[input.projectType] ?? [input.projectType.toLowerCase()];
  const hits = projectSignals.filter((signal) => haystack.includes(signal));
  if (hits.length > 0) {
    score += Math.min(28, hits.length * 7);
    reasons.push(`Projektart passt zu erkannten Signalen: ${hits.slice(0, 4).join(", ")}.`);
  } else {
    missing.push("Projektart genauer beschreiben, damit die fachliche Passung belastbarer wird.");
  }

  if (haystack.includes("kmu") || haystack.includes("kleine und mittlere") || haystack.includes("mittelstand")) {
    score += 18;
    reasons.push("Programm nennt KMU oder mittelständische Unternehmen.");
  } else if (input.companySize === "unknown") {
    missing.push("Unternehmensgröße fehlt.");
  } else {
    score += 4;
  }

  if (input.innovationLevel === "hoch" && /forschung|entwicklung|innovation|innovativ/i.test(haystack)) {
    score += 16;
    reasons.push("Innovations- oder F&E-Bezug passt zu einem anspruchsvollen KI-Projekt.");
  } else if (input.innovationLevel === "unknown") {
    missing.push("Innovationsgrad fehlt.");
  }

  const costHits = input.costTypes.filter((cost) => haystack.includes(cost));
  if (costHits.length > 0) {
    score += Math.min(12, costHits.length * 6);
    reasons.push(`Kostenarten erscheinen anschlussfähig: ${costHits.join(", ")}.`);
  } else {
    missing.push("Kostenarten müssen mit dem Programm abgeglichen werden.");
  }

  score += Math.min(18, Math.round(program.aiRelevance / 6));
  if (program.aiRelevance >= 55) {
    reasons.push("Hoher Digitalisierungs-, Daten-, Software- oder Innovationsbezug in der Quelle.");
  }

  if (program.deadlineStatus.toLowerCase().includes("geschlossen")) {
    score -= 30;
    reasons.push("Friststatus muss geprüft werden.");
  }

  const label = labelFor(score, missing.length);
  return {
    program,
    score: Math.max(0, Math.min(100, score)),
    label,
    reasons,
    missing,
    evidence: program.evidence.slice(0, 3)
  };
}

function labelFor(score: number, missingCount: number): MatchLabel {
  if (score >= 68 && missingCount <= 2) return "wahrscheinlich passend";
  if (score >= 42) return "möglicherweise passend";
  if (score <= 20) return "eher unpassend";
  return "unklar";
}

export function buildProjectBrief(input: MatchInput, matches: MatchResult[]): ProjectBrief {
  const best = matches[0];
  const unknown = (value: string) => value === "unknown" || value.trim() === "";

  return {
    title: "Projektsteckbrief für Förderreport",
    sections: [
      {
        title: "Ausgangslage",
        body: `Das Unternehmen möchte ein KI-Projekt im Bereich ${labelProjectType(input.projectType)} prüfen lassen. Branche: ${input.industry || "noch offen"}. Standort: ${input.region}.`,
        missing: !input.industry
      },
      {
        title: "Projektziel",
        body: `Ziel ist ein förderfähiges Vorhaben, das in der Phase "${input.stage === "unknown" ? "noch offen" : input.stage}" konkretisiert wird.`
      },
      {
        title: "Kostenrahmen",
        body: unknown(input.budget)
          ? "Der Kostenrahmen fehlt noch und muss für eine belastbare Förderprüfung ergänzt werden."
          : `Geplanter Budgetrahmen: ${input.budget}. Relevante Kostenarten: ${input.costTypes.join(", ") || "noch offen"}.`,
        missing: unknown(input.budget) || input.costTypes.length === 0
      },
      {
        title: "Förderlogik",
        body: best
          ? `Das aktuell stärkste Programm ist "${best.program.title}" mit Status "${best.label}". Wichtigste Begründung: ${best.reasons[0] ?? "Quelle prüfen."}`
          : "Es liegt noch kein belastbarer Programmtreffer vor.",
        missing: !best
      },
      {
        title: "Offene Angaben",
        body:
          best?.missing.length
            ? best.missing.join(" ")
            : "Für den nächsten Schritt sollten Angebot, technische Lösung, Kostenarten und Projektzeitplan dokumentiert werden."
      }
    ]
  };
}

export function labelProjectType(projectType: string): string {
  const labels: Record<string, string> = {
    rag: "Wissensassistent / RAG-System",
    chatbot: "KI-Chatbot oder Kundenservice-Automatisierung",
    automation: "Prozessautomatisierung",
    vision: "Computer Vision / Qualitätssicherung",
    forecast: "Datenanalyse und Prognose",
    ecommerce: "Digitaler Vertrieb oder Plattformprojekt"
  };
  return labels[projectType] ?? projectType;
}
